import * as CookieConsent from 'vanilla-cookieconsent';

import 'vanilla-cookieconsent/dist/cookieconsent.css';

declare global {
    interface Window {
        dataLayer?: unknown[];
    }
}

// Google Consent Mode requires the `arguments` object — gtag.js ignores plain arrays
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]): void {
    window.dataLayer = window.dataLayer ?? [];
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
}

function updateGoogleConsent(): void {
    const analyticsGranted = CookieConsent.acceptedCategory('analytics');
    const marketingGranted = CookieConsent.acceptedCategory('marketing');

    gtag('consent', 'update', {
        analytics_storage: analyticsGranted ? 'granted' : 'denied',
        ad_storage: marketingGranted ? 'granted' : 'denied',
        ad_user_data: marketingGranted ? 'granted' : 'denied',
        ad_personalization: marketingGranted ? 'granted' : 'denied',
    });
}

interface ConsentTexts {
    title: string;
    description: string;
    acceptAll: string;
    rejectAll: string;
    showPreferences: string;
    privacyPolicy: string;
    preferencesTitle: string;
    savePreferences: string;
    close: string;
    necessaryTitle: string;
    necessaryDescription: string;
    analyticsTitle: string;
    analyticsDescription: string;
    marketingTitle: string;
    marketingDescription: string;
}

const TEXTS: Record<string, ConsentTexts> = {
    cs: {
        title: 'Používáme cookies',
        description: 'Nezbytné cookies potřebujeme pro fungování webu. Se souhlasem používáme také analytické '
            + 'cookies pro měření návštěvnosti a marketingové cookies pro reklamu. Volbu můžete kdykoli změnit '
            + 'přes odkaz Nastavení cookies v patičce webu.',
        acceptAll: 'Přijmout vše',
        rejectAll: 'Odmítnout',
        showPreferences: 'Nastavení',
        privacyPolicy: 'Zpracování osobních údajů',
        preferencesTitle: 'Nastavení cookies',
        savePreferences: 'Uložit nastavení',
        close: 'Zavřít',
        necessaryTitle: 'Nezbytné cookies',
        necessaryDescription: 'Zajišťují základní fungování webu (přihlášení, zabezpečení). Nelze je vypnout.',
        analyticsTitle: 'Analytické cookies',
        analyticsDescription: 'Pomáhají nám měřit návštěvnost a pochopit, jak web používáte (Google Analytics).',
        marketingTitle: 'Marketingové cookies',
        marketingDescription: 'Umožňují měřit účinnost a cílení reklamy (Google Ads, Sklik, Facebook, LinkedIn).',
    },
    en: {
        title: 'We use cookies',
        description: 'Essential cookies are required for the website to work. With your consent we also use '
            + 'analytics cookies to measure traffic and marketing cookies for advertising. You can change your '
            + 'choice at any time via the Cookie settings link in the footer.',
        acceptAll: 'Accept all',
        rejectAll: 'Reject',
        showPreferences: 'Settings',
        privacyPolicy: 'Processing of personal data',
        preferencesTitle: 'Cookie settings',
        savePreferences: 'Save settings',
        close: 'Close',
        necessaryTitle: 'Necessary cookies',
        necessaryDescription: 'Required for the basic functioning of the website (sign-in, security). '
            + 'They cannot be disabled.',
        analyticsTitle: 'Analytics cookies',
        analyticsDescription: 'Help us measure traffic and understand how you use the website (Google Analytics).',
        marketingTitle: 'Marketing cookies',
        marketingDescription: 'Allow us to measure and target advertising (Google Ads, Sklik, Facebook, LinkedIn).',
    },
    de: {
        title: 'Wir verwenden Cookies',
        description: 'Notwendige Cookies sind für den Betrieb der Website erforderlich. Mit Ihrer Einwilligung '
            + 'verwenden wir außerdem Analyse-Cookies zur Reichweitenmessung und Marketing-Cookies für Werbung. '
            + 'Sie können Ihre Auswahl jederzeit über den Link Cookie-Einstellungen in der Fußzeile ändern.',
        acceptAll: 'Alle akzeptieren',
        rejectAll: 'Ablehnen',
        showPreferences: 'Einstellungen',
        privacyPolicy: 'Verarbeitung personenbezogener Daten',
        preferencesTitle: 'Cookie-Einstellungen',
        savePreferences: 'Einstellungen speichern',
        close: 'Schließen',
        necessaryTitle: 'Notwendige Cookies',
        necessaryDescription: 'Gewährleisten die Grundfunktionen der Website (Anmeldung, Sicherheit). '
            + 'Sie können nicht deaktiviert werden.',
        analyticsTitle: 'Analyse-Cookies',
        analyticsDescription: 'Helfen uns, die Besucherzahlen zu messen und zu verstehen, wie Sie die Website '
            + 'nutzen (Google Analytics).',
        marketingTitle: 'Marketing-Cookies',
        marketingDescription: 'Ermöglichen die Messung und Ausrichtung von Werbung '
            + '(Google Ads, Sklik, Facebook, LinkedIn).',
    },
    pl: {
        title: 'Używamy plików cookie',
        description: 'Niezbędne pliki cookie są wymagane do działania strony. Za Twoją zgodą używamy również '
            + 'analitycznych plików cookie do pomiaru ruchu oraz marketingowych plików cookie do reklam. '
            + 'Wybór możesz zmienić w każdej chwili poprzez link Ustawienia plików cookie w stopce.',
        acceptAll: 'Zaakceptuj wszystkie',
        rejectAll: 'Odrzuć',
        showPreferences: 'Ustawienia',
        privacyPolicy: 'Przetwarzanie danych osobowych',
        preferencesTitle: 'Ustawienia plików cookie',
        savePreferences: 'Zapisz ustawienia',
        close: 'Zamknij',
        necessaryTitle: 'Niezbędne pliki cookie',
        necessaryDescription: 'Zapewniają podstawowe działanie strony (logowanie, bezpieczeństwo). '
            + 'Nie można ich wyłączyć.',
        analyticsTitle: 'Analityczne pliki cookie',
        analyticsDescription: 'Pomagają nam mierzyć ruch i zrozumieć, jak korzystasz ze strony (Google Analytics).',
        marketingTitle: 'Marketingowe pliki cookie',
        marketingDescription: 'Umożliwiają pomiar i targetowanie reklam (Google Ads, Sklik, Facebook, LinkedIn).',
    },
    sk: {
        title: 'Používame cookies',
        description: 'Nevyhnutné cookies potrebujeme pre fungovanie webu. So súhlasom používame aj analytické '
            + 'cookies na meranie návštevnosti a marketingové cookies na reklamu. Voľbu môžete kedykoľvek zmeniť '
            + 'cez odkaz Nastavenie cookies v pätičke webu.',
        acceptAll: 'Prijať všetko',
        rejectAll: 'Odmietnuť',
        showPreferences: 'Nastavenia',
        privacyPolicy: 'Spracovanie osobných údajov',
        preferencesTitle: 'Nastavenie cookies',
        savePreferences: 'Uložiť nastavenia',
        close: 'Zavrieť',
        necessaryTitle: 'Nevyhnutné cookies',
        necessaryDescription: 'Zabezpečujú základné fungovanie webu (prihlásenie, zabezpečenie). Nedajú sa vypnúť.',
        analyticsTitle: 'Analytické cookies',
        analyticsDescription: 'Pomáhajú nám merať návštevnosť a pochopiť, ako web používate (Google Analytics).',
        marketingTitle: 'Marketingové cookies',
        marketingDescription: 'Umožňujú merať účinnosť a cielenie reklamy (Google Ads, Sklik, Facebook, LinkedIn).',
    },
};

function buildTranslation(texts: ConsentTexts, privacyPolicyUrl: string): CookieConsent.Translation {
    return {
        consentModal: {
            title: texts.title,
            description: texts.description,
            acceptAllBtn: texts.acceptAll,
            acceptNecessaryBtn: texts.rejectAll,
            showPreferencesBtn: texts.showPreferences,
            footer: `<a href="${privacyPolicyUrl}">${texts.privacyPolicy}</a>`,
        },
        preferencesModal: {
            title: texts.preferencesTitle,
            acceptAllBtn: texts.acceptAll,
            acceptNecessaryBtn: texts.rejectAll,
            savePreferencesBtn: texts.savePreferences,
            closeIconLabel: texts.close,
            sections: [
                {
                    title: texts.necessaryTitle,
                    description: texts.necessaryDescription,
                    linkedCategory: 'necessary',
                },
                {
                    title: texts.analyticsTitle,
                    description: texts.analyticsDescription,
                    linkedCategory: 'analytics',
                },
                {
                    title: texts.marketingTitle,
                    description: texts.marketingDescription,
                    linkedCategory: 'marketing',
                },
            ],
        },
    };
}

export function initializeCookieConsent(): void {
    const privacyPolicyUrl = document.body.dataset.privacyPolicyUrl ?? '/data-processing-consent';

    const translations: Record<string, CookieConsent.Translation> = {};
    for (const [language, texts] of Object.entries(TEXTS)) {
        translations[language] = buildTranslation(texts, privacyPolicyUrl);
    }

    void CookieConsent.run({
        categories: {
            necessary: {
                enabled: true,
                readOnly: true,
            },
            analytics: {
                autoClear: {
                    cookies: [{name: /^_ga/}],
                },
            },
            marketing: {
                autoClear: {
                    cookies: [{name: /^_fbp/}, {name: /^_gcl/}, {name: /^li_/}],
                },
            },
        },
        guiOptions: {
            consentModal: {
                layout: 'box',
                position: 'bottom left',
                equalWeightButtons: true,
            },
            preferencesModal: {
                layout: 'box',
            },
        },
        onConsent: updateGoogleConsent,
        onChange: updateGoogleConsent,
        language: {
            default: 'cs',
            autoDetect: 'document',
            translations,
        },
    });
}
